// Defence-in-depth wrapper around `sonner`'s toast API + the shadcn
// `use-toast` hook. Coerces any non-string argument (string | object |
// Error | array | structured FastAPI envelope) into a readable message
// BEFORE it reaches the rendering layer.
//
// Why this exists: a launch-day incident showed a structured 422 error
// envelope being rendered as "[object Object]" on the driver active-trip
// screen. `formatApiError` in `lib/api.js` was hardened, but multiple
// toast surfaces exist in this codebase:
//   1. `import { toast } from "sonner"` — the primary surface
//   2. `import { useToast } from "@/hooks/use-toast"` — the shadcn queue
//   3. Custom payloads where `description` or `title` is an object
//
// We shim ALL of them at app boot via an import side-effect from `App.js`.

import { toast as sonnerToast } from "sonner";

const LEVELS = ["error", "warning", "info", "success", "message", "loading"];

export function coerceToString(value) {
  if (value == null) return "Something went wrong";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) return value.message || "Error";
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        item && typeof item.msg === "string" ? item.msg : coerceToString(item)
      )
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof value === "object") {
    // React element — render its children text if possible, otherwise skip.
    // Identified by `$$typeof: Symbol(react.element)`. Sonner already
    // renders React elements natively, so DON'T stringify them.
    if (value.$$typeof) return value;
    // FastAPI structured detail envelopes — prefer human-readable fields.
    if (typeof value.message === "string") return value.message;
    if (typeof value.msg === "string") return value.msg;
    if (typeof value.detail === "string") return value.detail;
    if (typeof value.error === "string") return value.error;
    if (typeof value.title === "string") return value.title;
    try {
      const json = JSON.stringify(value);
      // Avoid spamming long JSON payloads in the UI — truncate to 240 chars.
      return json.length > 240 ? json.slice(0, 237) + "…" : json;
    } catch {
      return "Something went wrong";
    }
  }
  return String(value);
}

function shimSonner() {
  if (sonnerToast.__safeShimInstalled) return;
  for (const level of LEVELS) {
    const original = sonnerToast[level];
    if (typeof original === "function") {
      sonnerToast[level] = (message, options) =>
        original(coerceToString(message), coerceOptionsBag(options));
    }
  }
  sonnerToast.__safeShimInstalled = true;
}

function coerceOptionsBag(options) {
  if (!options || typeof options !== "object") return options;
  const out = { ...options };
  // sonner accepts `description` either as a string or a React node.
  // If it's a plain object that isn't a React element, coerce it.
  if (out.description != null &&
      typeof out.description !== "string" &&
      !(typeof out.description === "object" && out.description.$$typeof)) {
    out.description = coerceToString(out.description);
  }
  return out;
}

shimSonner();

export { sonnerToast as toast };
